import { createFetchTransport } from './transport';
import { ApiError } from './errors';
import { createSseReplyStream, readNdjsonStream } from './stream';
export function createClient(transport, replyStreamFactory) {
    return {
        conversations: {
            create: (req, signal) => transport.request('POST', '/v1/conversations', { body: req ?? {}, signal }),
            list: (params, signal) => transport.request('GET', '/v1/conversations', {
                query: { pageSize: params?.pageSize, pageToken: params?.pageToken, agentId: params?.agentId },
                signal,
            }),
            get: (conversationId, signal) => transport.request('GET', `/v1/conversations/${encodeURIComponent(conversationId)}`, { signal }),
            messages: (conversationId, params, signal) => transport.request('GET', `/v1/conversations/${encodeURIComponent(conversationId)}/messages`, {
                query: {
                    pageSize: params?.pageSize,
                    beforeMessageId: params?.beforeMessageId,
                    threadId: params?.threadId,
                    agentId: params?.agentId,
                },
                signal,
            }),
            resolveConversation: (agentId, signal) => transport.request('POST', '/v1/conversations:resolve', { body: { agentId }, signal }),
            // Both are COLON custom-verb paths, and that is load-bearing rather than
            // stylistic: the slash forms sit beside the shipped
            // `/v1/conversations/{conversation_id}`, where grpc-gateway dispatches
            // first-match-wins over a prepend-registered list. `:pair` / `:app-dm` have a
            // different component count and cannot collide in any registration order.
            // `/v1/conversations/app-dm` was the previous value here and is exactly that
            // hazard; `GET /v1/conversations` was the previous value for the pair listing
            // and is worse — a REAL route, so it answered with the whole unfiltered,
            // untagged listing instead of erroring.
            listThreadPair: (agentId, signal) => transport.request('GET', '/v1/conversations:pair', { query: { agentId }, signal }),
            mintAppDm: (agentId, signal) => transport.request('POST', '/v1/conversations:app-dm', { body: { agentId }, signal }),
            presignAttachment: (req, signal) => transport.request('POST', '/v1/attachments:presign', { body: req, signal }),
            // Mirror of the upload leg; the post-verb ":presign-download" distinguishes
            // it from the upload route, so the path does not collide with the existing
            // /v1/attachments:presign handler (which is UPLOAD-only).
            presignAttachmentDownload: (req, signal) => transport.request('POST', '/v1/attachments:presign-download', { body: req, signal }),
        },
        linkage: {
            status: (signal) => transport.request('GET', '/v1/linkage/status', { signal }),
        },
        bots: {
            list: (params, signal) => transport.request('GET', '/v1/bots', { query: { agentId: params?.agentId }, signal }),
            // `body: req` — the token stays in the body. See the interface docblock.
            connect: (req, signal) => transport.request('POST', '/v1/bots:connect', { body: req, signal }),
            activate: (botTokenHash, signal) => transport.request('POST', `/v1/bots/${encodeURIComponent(botTokenHash)}:activate`, {
                signal,
            }),
            disconnect: (botTokenHash, signal) => transport.request('DELETE', `/v1/bots/${encodeURIComponent(botTokenHash)}`, { signal }),
            setCompliance: (connectedBotId, req, signal) => transport.request('POST', `/v1/bots/${encodeURIComponent(connectedBotId)}:setCompliance`, {
                body: req,
                signal,
            }),
            setAttribution: (connectedBotId, req, signal) => transport.request('POST', `/v1/bots/${encodeURIComponent(connectedBotId)}:setAttribution`, {
                body: req,
                signal,
            }),
        },
        accounts: {
            register: (req, signal) => transport.request('POST', '/v1/accounts:register', { body: req, signal }),
            preview: (signal) => transport.request('GET', '/v1/accounts:preview', { signal }),
            complete: (req, signal) => transport.request('POST', '/v1/accounts:complete', { body: req, signal }),
        },
        profile: {
            get: (signal) => transport.request('GET', '/v1/me/profile', { signal }),
            update: (req, opts, signal) => transport.request('PATCH', '/v1/me/profile', {
                body: req,
                headers: opts?.idToken ? { 'X-ID-Token': opts.idToken } : undefined,
                signal,
            }),
            unlinkTelegram: (signal) => transport.request('POST', '/v1/me/telegram:unlink', { signal }),
        },
        consents: {
            list: (params, signal) => transport.request('GET', '/v1/me/consents', { query: { tenant_id: params?.tenantId }, signal }),
            accept: (req, signal) => transport.request('POST', '/v1/me/consents:accept', { body: req, signal }),
            withdraw: (req, signal) => transport.request('POST', '/v1/me/consents:withdraw', { body: req, signal }),
        },
        accountContact: {
            // Empty request messages by design (ADR-012 claims-derived identity) — body: {}.
            register: (signal) => transport.request('POST', '/v1/account/contact', { body: {}, signal }),
            get: (signal) => transport.request('GET', '/v1/account/contact', { signal }),
            revoke: (signal) => transport.request('DELETE', '/v1/account/contact', { signal }),
        },
        agents: {
            list: (params, signal) => transport.request('GET', '/v1/agents', {
                query: {
                    pageSize: params?.pageSize,
                    pageToken: params?.pageToken,
                    includeInactive: params?.includeInactive,
                },
                signal,
            }),
            get: (agentId, signal) => transport.request('GET', `/v1/agents/${encodeURIComponent(agentId)}`, { signal }),
            clone: (req, signal) => transport.request('POST', '/v1/agents:clone', { body: req, signal }),
            setShareLink: (agentId, enable, signal) => transport.request('POST', `/v1/agents/${encodeURIComponent(agentId)}:setShareLink`, {
                body: { agentId, enable },
                signal,
            }),
        },
        messages: {
            send: (req, signal) => transport.request('POST', '/v1/messages', { body: req, signal }),
            sendStream: (req) => {
                if (!replyStreamFactory) {
                    throw new ApiError('sendStream needs a ReplyStreamFactory (live client) — mock consumers supply their own stream', 0);
                }
                return replyStreamFactory(req);
            },
        },
        uploads: {
            presign: (req, signal) => transport.request('POST', '/v1/uploads:presign', { body: req, signal }),
            putToPresignedUrl: async (presign, file, signal) => {
                const res = await fetch(presign.uploadUrl, {
                    method: 'PUT',
                    headers: presign.headers ?? {},
                    body: file,
                    signal,
                });
                if (!res.ok) {
                    throw new ApiError(`upload PUT failed: HTTP ${res.status}`, res.status);
                }
            },
        },
        usage: {
            getMy: (window, signal) => transport.request('GET', '/v1/usage', { query: { window }, signal }),
        },
        knowledge: {
            confirmUpload: (req, signal) => transport.request('POST', `/v1/uploads/${encodeURIComponent(req.fileId)}:confirm`, { body: req, signal }),
            ingest: (req, signal) => transport.request('POST', '/v1/knowledge:ingest', { body: req, signal }),
            ingestText: (req, signal) => transport.request('POST', '/v1/knowledge:ingestText', { body: req, signal }),
            list: (agentId, params, signal) => transport.request('GET', `/v1/agents/${encodeURIComponent(agentId)}/knowledge`, {
                query: { pageSize: params?.pageSize, pageToken: params?.pageToken },
                signal,
            }),
            deleteItem: (agentId, knowledgeId, signal) => transport.request('DELETE', `/v1/agents/${encodeURIComponent(agentId)}/knowledge/${encodeURIComponent(knowledgeId)}`, { signal }),
        },
        agentAdmin: {
            create: (req, signal) => transport.request('POST', '/v1/agents', { body: req, signal }),
            // PATCH body is the Agent itself (proto body: "agent"); update_mask rides the query
            update: (req, signal) => transport.request('PATCH', `/v1/agents/${encodeURIComponent(req.agent.agentId)}`, {
                body: req.agent,
                query: { updateMask: req.updateMask },
                signal,
            }),
            generateFromDescription: (req, signal) => transport.requestStream
                ? transport.requestStream('POST', '/v1/agents:generateFromDescription', { body: req, signal })
                : (() => {
                    throw new ApiError('generateFromDescription needs a streaming transport (live client)', 0);
                })(),
            listAbilities: (agentId, signal) => transport.request('GET', `/v1/agents/${encodeURIComponent(agentId)}/abilities`, { signal }),
            setAbilities: (agentId, assignments, signal) => transport.request('POST', `/v1/agents/${encodeURIComponent(agentId)}/abilities:set`, {
                body: { assignments },
                signal,
            }),
        },
        health: (signal) => transport.request('GET', '/v1/health', { signal }),
        auth: {
            telegramSession: (req, signal) => transport.request('POST', '/v1/auth/telegram:session', { body: req, signal }),
            telegramRefresh: (req, signal) => transport.request('POST', '/v1/auth/telegram:refresh', { body: req, signal }),
        },
        goals: {
            create: (req, signal) => transport.request('POST', '/v1/goals', { body: req, signal }),
            get: (req, signal) => transport.request('GET', `/v1/goals/${encodeURIComponent(req.goalId)}`, {
                query: { agentId: req.agentId },
                signal,
            }),
            getCurrent: (req, signal) => transport.request('GET', '/v1/goals:current', { query: { agentId: req.agentId }, signal }),
            listEvents: (req, signal) => transport.request('GET', `/v1/goals/${encodeURIComponent(req.goalId)}/events`, {
                query: {
                    agentId: req.agentId,
                    kinds: req.kinds,
                    pageSize: req.pageSize,
                    pageToken: req.pageToken,
                },
                signal,
            }),
            accept: (req, signal) => transport.request('POST', `/v1/goals/${encodeURIComponent(req.goalId)}:accept`, { body: req, signal }),
            pause: (req, signal) => transport.request('POST', `/v1/goals/${encodeURIComponent(req.goalId)}:pause`, { body: req, signal }),
            resume: (req, signal) => transport.request('POST', `/v1/goals/${encodeURIComponent(req.goalId)}:resume`, { body: req, signal }),
            cancel: (req, signal) => transport.request('POST', `/v1/goals/${encodeURIComponent(req.goalId)}:cancel`, { body: req, signal }),
            achieve: (req, signal) => transport.request('POST', `/v1/goals/${encodeURIComponent(req.goalId)}:achieve`, { body: req, signal }),
            markImpossible: (req, signal) => transport.request('POST', `/v1/goals/${encodeURIComponent(req.goalId)}:impossible`, { body: req, signal }),
            replace: (req, signal) => transport.request('POST', `/v1/goals/${encodeURIComponent(req.goalId)}:replace`, { body: req, signal }),
            deleteHistory: (req, signal) => transport.request('POST', `/v1/goals/${encodeURIComponent(req.goalId)}:deleteHistory`, {
                body: req,
                signal,
            }),
            confirmMilestone: (req, signal) => transport.request('POST', `/v1/goals/${encodeURIComponent(req.goalId)}/events:confirmMilestone`, { body: req, signal }),
            rejectMilestone: (req, signal) => transport.request('POST', `/v1/goals/${encodeURIComponent(req.goalId)}/events:rejectMilestone`, { body: req, signal }),
            setSuggestionState: (req, signal) => transport.request('POST', `/v1/goals/${encodeURIComponent(req.goalId)}:setSuggestionState`, {
                body: req,
                signal,
            }),
        },
        widgetInstallations: {
            list: (signal) => transport.request('GET', '/v1/widget-installations', { signal }),
            get: (installationId, signal) => transport.request('GET', `/v1/widget-installations/${encodeURIComponent(installationId)}`, { signal }),
            create: (req, signal) => transport.request('POST', '/v1/widget-installations', { body: req, signal }),
            // `installationId` rides in the body as well as the path (`body: "*"` binds the path field from
            // the URL and tolerates it in the body) — the same shape `agents.setShareLink` sends.
            setAllowedOrigins: (req, signal) => transport.request('POST', `/v1/widget-installations/${encodeURIComponent(req.installationId)}:setAllowedOrigins`, { body: req, signal }),
            setEnabled: (req, signal) => transport.request('POST', `/v1/widget-installations/${encodeURIComponent(req.installationId)}:setEnabled`, {
                body: req,
                signal,
            }),
        },
    };
}
/** Convenience: fetch-transport client in one call (wires the live SSE reply stream). */
export function createFetchClient(cfg) {
    return createClient(createFetchTransport(cfg), (body) => createSseReplyStream(cfg, body));
}
export { readNdjsonStream };
//# sourceMappingURL=client.js.map